import { z } from 'zod';
import { TicketKeySchema } from './tickets';

const LinkTypeNameSchema = z.string().trim().min(1).max(40);
const LinkLabelSchema = z.string().trim().min(1).max(60);

/** Typed relation (S-05-10): `outward` read from the source, `inward` from the target. */
export const LinkTypeSchema = z.object({
  id: z.uuid(),
  name: LinkTypeNameSchema,
  outward: LinkLabelSchema,
  inward: LinkLabelSchema,
  isSystem: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type LinkType = z.infer<typeof LinkTypeSchema>;

export const CreateLinkTypeSchema = z.strictObject({
  name: LinkTypeNameSchema,
  outward: LinkLabelSchema,
  inward: LinkLabelSchema,
});
export type CreateLinkType = z.infer<typeof CreateLinkTypeSchema>;

export const UpdateLinkTypeSchema = CreateLinkTypeSchema.partial();
export type UpdateLinkType = z.infer<typeof UpdateLinkTypeSchema>;

/** Seeded per tenant with `isSystem: true`; `clones` is written by the clone endpoint. */
export const SYSTEM_LINK_TYPES = [
  { name: 'relates', outward: 'relates to', inward: 'relates to' },
  { name: 'blocks', outward: 'blocks', inward: 'is blocked by' },
  { name: 'duplicates', outward: 'duplicates', inward: 'is duplicated by' },
  { name: 'clones', outward: 'clones', inward: 'is cloned by' },
] as const satisfies readonly CreateLinkType[];
export type SystemLinkTypeName = (typeof SYSTEM_LINK_TYPES)[number]['name'];

export const LINK_DIRECTIONS = ['outward', 'inward'] as const;
export const LinkDirectionSchema = z.enum(LINK_DIRECTIONS);
export type LinkDirection = z.infer<typeof LinkDirectionSchema>;

/** The ticket at the other end of a link, as much as a list row needs. */
export const LinkedTicketSchema = z.object({
  id: z.uuid(),
  key: TicketKeySchema,
  summary: z.string(),
  statusId: z.uuid(),
});
export type LinkedTicket = z.infer<typeof LinkedTicketSchema>;

/**
 * A link as seen from one ticket: `direction` tells which end the viewer is, `label` is the
 * matching outward/inward text and `ticket` is the other end.
 */
export const TicketLinkSchema = z.object({
  id: z.uuid(),
  linkTypeId: z.uuid(),
  linkTypeName: z.string(),
  direction: LinkDirectionSchema,
  label: z.string(),
  sourceTicketId: z.uuid(),
  targetTicketId: z.uuid(),
  ticket: LinkedTicketSchema,
  createdAt: z.iso.datetime(),
});
export type TicketLink = z.infer<typeof TicketLinkSchema>;

/** `POST /tickets/{id}/links`; the target must be visible to the caller (404 otherwise). */
export const CreateLinkSchema = z.strictObject({
  linkTypeId: z.uuid(),
  targetTicketId: z.uuid(),
});
export type CreateLink = z.infer<typeof CreateLinkSchema>;

/** `GET /tickets/{id}/links` (S-05-10): every link the caller may see, from the ticket's point of view. */
export const TicketLinkListResponseSchema = z.object({ items: z.array(TicketLinkSchema) });
export type TicketLinkListResponse = z.infer<typeof TicketLinkListResponseSchema>;

/** `GET /link-types` (portal): the tenant's link types, system ones first. */
export const LinkTypeListResponseSchema = z.object({ items: z.array(LinkTypeSchema) });
export type LinkTypeListResponse = z.infer<typeof LinkTypeListResponseSchema>;
